import { ReactElement, useState } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { mixed, object } from 'yup'
import type { OrganisationDTO } from '@api/fastAPI.schemas'
import { useCreateMembership } from '@api/user-management/user-management'
import { AddButton } from '@ui/actions'
import { Form2 } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import type { AddMembershipFormValues } from '../../types'
import AddMembershipFields from '../AddMembershipFields'

const addMembershipDefaultValues: AddMembershipFormValues = {
  organisation: null,
  isAdmin: false,
}

const addMembershipValidationSchema = object({
  organisation: mixed().nullable().required('Organisation ist erforderlich'),
})

interface AddMembershipFormProps {
  userId: string
  joinableOrganisations: OrganisationDTO[]
  onAdded: () => Promise<void>
}

const AddMembershipForm = ({
  userId,
  joinableOrganisations,
  onAdded,
}: AddMembershipFormProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  // Remounts the form (and clears its fields) after a successful add.
  const [resetKey, setResetKey] = useState(0)

  const { mutate: createMembership, isLoading: isAdding } = useCreateMembership<
    AxiosError,
    unknown
  >({
    mutation: {
      meta: {
        title: 'Organisation zuweisen',
        description: 'Fügt den Nutzer einer weiteren Organisation hinzu',
      },
      onSuccess: async () => {
        await onAdded()
        setResetKey(key => key + 1)
        enqueueSnackbar('Organisation zugewiesen', { variant: 'success' })
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Zuweisen der Organisation'), {
          variant: 'error',
        })
      },
    },
  })

  return (
    <Form2<AddMembershipFormValues>
      key={resetKey}
      defaultValues={addMembershipDefaultValues}
      validationSchema={addMembershipValidationSchema}
      onSubmit={values => {
        if (!values.organisation) return

        createMembership({
          data: {
            user_id: userId,
            organisation_guid: values.organisation.guid,
            is_admin: values.isAdmin,
          },
        })
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <AddMembershipFields joinableOrganisations={joinableOrganisations} />
        <AddButton size="small" type="submit" disabled={isAdding} data-cy="btn-add-membership">
          Hinzufügen
        </AddButton>
      </Box>
    </Form2>
  )
}

export default AddMembershipForm
